// src/pages/providers/2_dashboard/utils/patientSetupValidation.js
//
// Pure, dependency-free validation helpers for the patient setup wizard
// (PatientSetupForm). They are deliberately kept out of the component so they
// can be unit-tested in isolation (the component itself is tightly coupled to
// Firebase, react-select and GlobalStateContext, which makes it expensive to
// render in tests).
//
// Design note (why these exist):
// The wizard previously duplicated the same per-field validity rules in two
// places — the "Next" button gate (isStepValid) and the inline "please
// complete the following" message builder (getMissingRequiredFields). Both
// hard-coded a bespoke rule ("the Diagnostic Decision field must have exactly
// two clinicians"). We now express that requirement declaratively in the org
// setup config via `minSelections`, so each organisation states its own rule
// (orgs on the default config keep minSelections: 2). Well Life Clinic no
// longer collects clinicians at setup (deferred assignment). This module is
// the single source of truth both callers delegate to.

/**
 * Validate an email address.
 *
 * An empty value is treated as valid here (the "is this field required?" check
 * is handled by the caller); a non-empty value must match a basic RFC-ish
 * pattern. Kept here so the wizard has one definition shared by the form and
 * the validation helpers.
 *
 * @param {string} email
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  if (!email) return true;
  const trimmed = email.trim().toLowerCase();
  // Structural guards -- reject patterns that Firebase Auth would reject with
  // INVALID_EMAIL so the user sees an inline form error, not a server 500.
  // These mirror the five guards in validate_email_format (clinician_email_update.py)
  // and validateEmail (informantValidation.js).
  if (trimmed.length > 254) return false;
  if (/^[.,]/.test(trimmed) || /[.,]$/.test(trimmed)) return false;
  if (trimmed.includes('..')) return false;
  if ((trimmed.match(/@/g) || []).length !== 1) return false;
  const [local, domain] = trimmed.split('@');
  if (local.endsWith('.') || domain.startsWith('.')) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
};

/**
 * The minimum number of selections a `selectMultipleButtons` field requires.
 *
 * Driven by the field's `minSelections` config so the rule lives with the org
 * setup config rather than being hard-coded against a specific varName here.
 * Defaults to 1 (i.e. "at least one selected") when not specified, matching the
 * generic behaviour of a multi-select field.
 *
 * @param {object} field - A field config object.
 * @returns {number} A positive integer minimum (>= 1).
 */
export const getMinSelections = (field) => {
  const min = Number(field?.minSelections);
  return Number.isFinite(min) && min >= 1 ? min : 1;
};

/**
 * Whether a field's ``enabledWhen`` condition is currently satisfied.
 *
 * ``enabledWhen`` is an optional config property on a field that makes it
 * conditionally required and visible based on another field's value:
 *
 *   enabledWhen: { field: "assessmentType", equals: "National RTC" }
 *
 * Or matching any of several values:
 *
 *   enabledWhen: { field: "assessmentType", oneOf: ["National RTC", "Hampshire IOW"] }
 *
 * When the condition is not met the field is greyed-out in the UI and
 * skipped during validation — its value is irrelevant.
 *
 * The parent field's value in formData follows react-select conventions:
 * single-select dropdowns store the selection as an array of one option
 * object (e.g. [{ value: "National RTC", label: "..." }]).  Plain string
 * values are also supported for non-dropdown parents.
 *
 * @param {object} field - Field config (may have an ``enabledWhen`` property).
 * @param {object} formData - Current wizard form state.
 * @returns {boolean} True when enabled (or when there is no condition).
 */
export const isFieldEnabled = (field, formData = {}) => {
  const cond = field?.enabledWhen;
  if (!cond) return true;

  const parentValue = formData[cond.field];

  // Resolve the raw string value from whichever react-select shape we get.
  let resolved;
  if (Array.isArray(parentValue) && parentValue.length > 0) {
    resolved = parentValue[0]?.value;
  } else if (parentValue && typeof parentValue === 'object' && 'value' in parentValue) {
    resolved = parentValue.value;
  } else {
    resolved = parentValue;
  }

  // Support both single-value (equals) and multi-value (oneOf) conditions.
  if (Array.isArray(cond.oneOf)) {
    return cond.oneOf.includes(resolved);
  }
  return resolved === cond.equals;
};

/**
 * After a parent field changes, wipe any dependent fields whose ``enabledWhen``
 * condition no longer holds, OR whose parent moved to a different value that
 * maps to different dynamic options (``optionsByParent``). This prevents stale
 * subcontract values persisting when e.g. Contract Type moves between two
 * contracts that both collect subcontracts but from different option sets.
 *
 * Dropdown dependents are cleared to ``null`` for react-select; their
 * ``customFields`` entry is removed entirely so nothing is submitted.
 *
 * @param {string} changedFieldName - varName of the field the user just edited.
 * @param {object} formData - Draft form state including the parent change.
 * @param {object} orgConfig - Resolved org setup config (`steps` map).
 * @returns {object} Updated form state (shallow copy; mutates nested customFields copy).
 */
export const applyDependentFieldClears = (changedFieldName, formData, orgConfig) => {
  if (!orgConfig?.steps) return formData;

  const next = {
    ...formData,
    customFields: { ...(formData.customFields || {}) },
  };

  for (const step of Object.values(orgConfig.steps)) {
    for (const field of Object.values(step.fields || {})) {
      if (field?.enabledWhen?.field !== changedFieldName) continue;

      let shouldClear = false;

      if (!isFieldEnabled(field, next)) {
        // Condition no longer met — always clear.
        shouldClear = true;
      } else if (field.optionsByParent && next[field.varName] != null) {
        // Condition still met, but the parent drives different option sets.
        // If the current dependent value is not in the new option set, clear it.
        const parentVal = next[changedFieldName];
        const resolvedParent = Array.isArray(parentVal) && parentVal.length > 0
          ? parentVal[0]?.value
          : (parentVal && typeof parentVal === 'object' && 'value' in parentVal)
            ? parentVal.value
            : parentVal;
        const newOptions = field.optionsByParent[resolvedParent] || field.options || [];
        const currentVal = Array.isArray(next[field.varName]) && next[field.varName].length > 0
          ? next[field.varName][0]?.value
          : next[field.varName];
        if (!newOptions.some((opt) => opt.value === currentVal)) {
          shouldClear = true;
        }
      }

      if (!shouldClear) continue;

      if (field.inputType === 'selectOneDropdown') {
        next[field.varName] = null;
      } else {
        next[field.varName] = undefined;
      }
      delete next.customFields[field.varName];
    }
  }

  return next;
};

/**
 * Is a single field's current value valid for its input type?
 *
 * Callers are responsible for deciding whether a field needs validating at all
 * (i.e. skipping non-required fields, requiredOneOf group members, and the
 * demo/test field in production). This function assumes the field IS required
 * and answers only "does the current value satisfy this field's type rule?".
 *
 * @param {object} field - Field config ({ varName, inputType, minSelections }).
 * @param {*} value - The current value from formData for this field.
 * @returns {boolean}
 */
export const isFieldValueValid = (field, value) => {
  if (!field) return true;

  // Email must be present AND well-formed when required.
  if (field.varName === 'email') {
    return Boolean(value) && validateEmail(value);
  }

  switch (field.inputType) {
    case 'selectMultipleDropdown':
      // At least one option selected.
      return Array.isArray(value) && value.length > 0;

    case 'selectOneDropdown':
      // Stored as a one-item array of option objects, or occasionally a
      // single option object. Either shape counts as "filled".
      return Array.isArray(value) ? value.length > 0 : Boolean(value);

    case 'selectOneButtons':
      // A single button selection (object) or null when nothing is chosen.
      return value !== undefined && value !== null;

    case 'selectMultipleButtons':
      // Config-driven minimum (e.g. MDT review: lead + one reviewer => 2, or
      // lead-only at setup => 1). The UI separately caps the maximum.
      return Array.isArray(value) && value.length >= getMinSelections(field);

    default:
      // Free-text and everything else: just needs to be non-empty.
      return value !== undefined && value !== '';
  }
};

/**
 * Validate all fields in a single wizard step.
 *
 * Returns both an overall validity boolean (used to enable/disable the "Next"
 * button) and the list of human-readable missing-field labels (used for the
 * inline error message), so the two share exactly one implementation.
 *
 * Behaviour preserved from the original inline logic:
 *   - The demo/test patient-type field is skipped entirely in production.
 *   - Only fields with `required: true` are individually validated.
 *   - Fields tagged with `requiredOneOf` are validated as a group (at least one
 *     member of each group must have a value) rather than individually.
 *
 * @param {object} stepConfig - orgConfig.steps[key] (expects a `fields` map).
 * @param {object} formData - Current wizard form state.
 * @param {object} [options]
 * @param {boolean} [options.isProduction=false] - Skip demo/test validation in prod.
 * @returns {{ isValid: boolean, missingFields: string[] }}
 */
export const getStepValidation = (stepConfig, formData = {}, { isProduction = false } = {}) => {
  const fields = stepConfig?.fields;

  // Placeholder / empty steps are always valid.
  if (!fields || Object.keys(fields).length === 0) {
    return { isValid: true, missingFields: [] };
  }

  const missingFields = [];
  const oneOfGroups = {};

  Object.values(fields).forEach((field) => {
    if (!field) return;

    // Demo/test patient type is not shown or validated in production.
    if (isProduction && field.varName === 'demoOrTest') return;

    // Conditionally-enabled fields are skipped when their enabledWhen
    // condition is not met — the UI disables them, so there is nothing
    // for the user to fill in and nothing to validate.
    if (field.enabledWhen && !isFieldEnabled(field, formData)) return;

    // requiredOneOf members are validated as a group after this loop.
    if (field.requiredOneOf) {
      if (!oneOfGroups[field.requiredOneOf]) oneOfGroups[field.requiredOneOf] = [];
      oneOfGroups[field.requiredOneOf].push(field);
      return;
    }

    // Non-required fields never contribute a "missing field".
    if (field.required !== true) return;

    const value = formData[field.varName];
    if (isFieldValueValid(field, value)) return;

    // For multi-select fields that need more than one selection, give a
    // specific "select at least N" hint including the current count.
    if (field.inputType === 'selectMultipleButtons' && getMinSelections(field) > 1) {
      const min = getMinSelections(field);
      const count = Array.isArray(value) ? value.length : 0;
      missingFields.push(`${field.displayText} (select at least ${min} clinicians, currently ${count})`);
    } else {
      missingFields.push(field.displayText);
    }
  });

  // requiredOneOf: at least one field in each group must have a value.
  Object.values(oneOfGroups).forEach((groupFields) => {
    const groupFilled = groupFields.some((field) => {
      const value = formData[field.varName];
      return value !== undefined && value !== '' && value !== null;
    });
    if (!groupFilled) {
      const names = groupFields.map((f) => f.displayText).join(' or ');
      missingFields.push(`${names} (at least one required)`);
    }
  });

  return { isValid: missingFields.length === 0, missingFields };
};
